import { DataTable } from '@/components'
import { cn } from '@/lib/utils'
import type { IssueOrderFields } from '@/modules/issues'
import { UserProfileLink } from '@/modules/users/UserProfile/UserProfileLink'
import { format, getLocale } from '@/utils/date-fns'
import { getNodes } from '@/utils/graphql'
import {
  useAdmin,
  useCompany,
  useInfiniteScroll,
  useOnErrorAuto,
  usePermissions,
} from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import type { SortingState } from '@tanstack/react-table'
import { createColumnHelper } from '@tanstack/react-table'
import { useDebounce, useTranslate } from '@ur-solutions/react-hooks'
import { addDays, formatISO, startOfDay } from 'date-fns'
import React, { useCallback, useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { ALL_ISSUES_QUERY } from '../../queries'
import {
  IssueTableColumn,
  relationValueFor,
  useIssueTableColumns,
} from '../../table-config'
import type { IssuesTableFilter } from '../../types'
import type { IssueNodeForAllIssues } from '../../types.graphl'
import { TagBadge } from '../tag-picker/tag-badge'
import { IssueRowMenu } from './issue-row-menu'
import {
  IssueCategoryBadge,
  IssueDateCell,
  IssueReferenceCell,
  IssueStatusCell,
  VehiclePlate,
} from './issue-table-cells'
import { IssuesCards } from './IssuesCards'

/** The field the server orders by, per sortable column. */
const ORDER_FIELDS = {
  [IssueTableColumn.DATE]: 'timeOfIssueStart',
  [IssueTableColumn.EMPLOYEE]: 'user__firstName',
  [IssueTableColumn.CATEGORY]: 'category__name',
  [IssueTableColumn.RISK_ASPECT]: 'riskAspect__name',
  [IssueTableColumn.SEVERITY]: 'severity',
  [IssueTableColumn.CREATED_AT]: 'createdAt',
} as const satisfies Record<string, IssueOrderFields>

const DEFAULT_ORDER_BY: IssueOrderFields = '-timeOfIssueStart'

/** A day, or a dash when the issue has no value for it. */
function formatDay(value: string | null | undefined): string {
  if (!value) return '-'
  return format(new Date(value), 'PP')
}

function formatCost(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `${value.toLocaleString(getLocale().code)} NOK`
}

interface IssuesTableProps {
  filter: IssuesTableFilter

  onSort: (orderBy: IssueOrderFields) => void
  onFilterUpdate?: (info: { count: number | undefined }) => void
}

export const IssuesTable: React.FC<IssuesTableProps> = ({
  filter,

  onSort,
  onFilterUpdate,
}) => {
  const translations = useTranslate({
    date: 'common.date',
    title: 'issues.title',
    tags: 'common.tags',
    responsible: 'common.responsible',
    caseHandler: 'common.case-handler',
    process: 'issues.process',
    subcategory: 'common.sub-category',
    location: 'common.location',
    created: 'common.created',
    dateEnd: 'common.end-date',
    estimatedCost: 'issues.cost-estimation',
    actualCost: 'issues.actual-cost',
    dateClosed: 'issues.date-closed',
    freightCarrier: 'common.freight-carrier',
    reference: ['issues.reference', { abbreviate: false }],
    employee: 'common.submitter',
    category: 'common.category',
    status: 'common.status',
    severity: 'common.severity',
    riskAspect: 'common.risk-aspect',

    open: 'common.open-alt',
    archived: 'common.archived',
    closed: 'common.closed',
    started: 'issues.started',

    noIssues: 'issues.no-issues',
    noResults: 'common.no-results',

    severityGrades: {
      LOW: 'common.low',
      MEDIUM: 'common.medium',
      HIGH: 'common.high',
    },
  })

  const company = useCompany()
  const admin = useAdmin()
  const { isVisible, relationColumns } = useIssueTableColumns()
  const { hasPermissions } = usePermissions()
  const onErrorAuto = useOnErrorAuto()

  const canViewOthers = hasPermissions(
    PERMISSIONS.issues.view.othersissue,
    true
  )

  const debouncedQuery = useDebounce(filter.query)
  const debouncedExcludeCategories = useDebounce(filter.excludeCategories, 500)
  const debouncedStatuses = useDebounce(filter.statuses)
  const debouncedArchived = useDebounce(filter.archived)
  const debouncedTags = useDebounce(filter.tags, 500)

  const getDateFilters = () => {
    const start = formatISO(startOfDay(filter.dateRange.from))
    const end = formatISO(addDays(startOfDay(filter.dateRange.to), 1))

    return {
      startDate: start,
      endDate: end,
    }
  }

  const {
    data,
    loading: queryLoading,
    fetchMore,
  } = useQuery(ALL_ISSUES_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      q: debouncedQuery,
      orderBy: filter.orderBy,
      excludeCategories: debouncedExcludeCategories,
      includeSeverities: filter.severities,
      archivedAtIsNull: !debouncedArchived,
      includeStatuses: debouncedStatuses,
      first: DEFAULT_PAGE_SIZE,
      company: company.id,
      relatedInfringement: filter.relatedInfringement,
      onlyManuallyCreated: filter.onlyManuallyCreated,
      isInvolved:
        filter.involvement.length === 1
          ? filter.involvement[0] === 'INVOLVED'
          : undefined,
      tags: debouncedTags.length > 0 ? debouncedTags : undefined,
      ...(canViewOthers &&
        filter.leaderGroup !== null && {
          leaderGroupId: filter.leaderGroup,
        }),
      ...getDateFilters(),
    },
    onError: onErrorAuto(),
  })

  const issues = useMemo(() => getNodes(data?.allIssues), [data?.allIssues])
  const pageInfo = data?.allIssues.pageInfo

  const handleScrollBottom = useCallback(async () => {
    if (
      typeof data === 'undefined' ||
      typeof pageInfo === 'undefined' ||
      !pageInfo.hasNextPage
    )
      return
    try {
      await fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, fetchMore, pageInfo])

  useInfiniteScroll(
    handleScrollBottom,
    200,
    !queryLoading && pageInfo?.hasNextPage
  )

  const column = createColumnHelper<IssueNodeForAllIssues>()

  const {
    LOW: lowSeverity,
    MEDIUM: mediumSeverity,
    HIGH: highSeverity,
  } = translations.severityGrades
  const referenceHeader = translations.reference({ abbreviate: false })

  const columns = useMemo(
    () => [
      column.accessor('timeOfIssueStart', {
        id: IssueTableColumn.DATE,
        header: translations.date,
        meta: { narrow: true },
        enableSorting: true,
        cell: info => (
          <IssueDateCell
            issueId={info.row.original.id}
            timeOfIssue={info.getValue() as string}
          />
        ),
      }),
      column.accessor('vehiclePlateNumber', {
        id: IssueTableColumn.REG_NR,
        header: translations.freightCarrier,
        meta: { narrow: true },
        cell: info => <VehiclePlate plate={info.getValue()} />,
      }),
      column.display({
        id: IssueTableColumn.REFERENCE,
        header: referenceHeader,
        cell: info => (
          <IssueReferenceCell
            reference={info.row.original.referenceId}
            title={info.row.original.title}
          />
        ),
      }),
      column.accessor('user', {
        id: IssueTableColumn.EMPLOYEE,
        header: translations.employee,
        meta: { narrow: true },
        enableSorting: admin,
        cell: info => <UserProfileLink user={info.getValue()} />,
      }),
      column.accessor('category', {
        id: IssueTableColumn.CATEGORY,
        header: translations.category,
        meta: { narrow: true },
        enableSorting: true,
        cell: info => <IssueCategoryBadge category={info.getValue()} />,
      }),
      column.accessor('riskAspect', {
        id: IssueTableColumn.RISK_ASPECT,
        header: translations.riskAspect,
        meta: { narrow: true },
        enableSorting: true,
        cell: info => info.getValue()?.name ?? '-',
      }),
      column.accessor('severity', {
        id: IssueTableColumn.SEVERITY,
        header: translations.severity,
        meta: { narrow: true },
        enableSorting: true,
        cell(info) {
          const severity = info.getValue()
          if (!severity) return '-'

          return {
            LOW: lowSeverity,
            MEDIUM: mediumSeverity,
            HIGH: highSeverity,
          }[severity]
        },
      }),
      column.display({
        id: IssueTableColumn.STATUS,
        header: translations.status,
        meta: { narrow: true },
        cell: info => (
          <IssueStatusCell
            closed={info.row.original.closed}
            archived={info.row.original.archived}
            edited={info.row.original.edited}
            editText={translations.started}
            openText={translations.open}
            archivedText={translations.archived}
            closedText={translations.closed}
          />
        ),
      }),

      // Columns a company can switch on, in the order they render after the
      // built-ins.
      column.accessor('title', {
        id: IssueTableColumn.TITLE,
        header: translations.title,
        cell: info => info.getValue() || '-',
      }),
      column.display({
        id: IssueTableColumn.TAGS,
        header: translations.tags,
        cell(info) {
          const tags = getNodes(info.row.original.tags)
          if (tags.length === 0) return '-'

          return (
            <span className="flex flex-wrap gap-1">
              {tags.map(tag => (
                <TagBadge key={tag.id} tag={tag} variant="pill" />
              ))}
            </span>
          )
        },
      }),
      column.accessor('responsibleUser', {
        id: IssueTableColumn.RESPONSIBLE_USER,
        header: translations.responsible,
        meta: { narrow: true },
        cell: info => info.getValue()?.fullName ?? '-',
      }),
      column.accessor('caseHandler', {
        id: IssueTableColumn.CASE_HANDLER,
        header: translations.caseHandler,
        meta: { narrow: true },
        cell: info => info.getValue()?.fullName ?? '-',
      }),
      column.accessor('processChoice', {
        id: IssueTableColumn.PROCESS,
        header: translations.process,
        meta: { narrow: true },
        cell: info => info.getValue()?.name ?? '-',
      }),
      column.accessor('subcategory', {
        id: IssueTableColumn.SUBCATEGORY,
        header: translations.subcategory,
        meta: { narrow: true },
        cell: info => info.getValue()?.name ?? '-',
      }),
      column.accessor('locationPlacename', {
        id: IssueTableColumn.LOCATION,
        header: translations.location,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('createdAt', {
        id: IssueTableColumn.CREATED_AT,
        header: translations.created,
        meta: { narrow: true },
        enableSorting: true,
        cell: info => formatDay(info.getValue()),
      }),
      column.accessor('timeOfIssueEnd', {
        id: IssueTableColumn.DATE_END,
        header: translations.dateEnd,
        meta: { narrow: true },
        cell: info => formatDay(info.getValue()),
      }),
      column.accessor('costEstimation', {
        id: IssueTableColumn.COST_ESTIMATION,
        header: translations.estimatedCost,
        meta: { narrow: true, end: true },
        cell: info => formatCost(info.getValue()),
      }),
      column.accessor('actualCost', {
        id: IssueTableColumn.ACTUAL_COST,
        header: translations.actualCost,
        meta: { narrow: true, end: true },
        cell: info => formatCost(info.getValue()),
      }),
      column.accessor('closedAt', {
        id: IssueTableColumn.CLOSED_AT,
        header: translations.dateClosed,
        meta: { narrow: true },
        cell: info => formatDay(info.getValue()),
      }),

      ...relationColumns.map(relation =>
        column.display({
          id: `relation:${relation.id}`,
          header: relation.name,
          meta: { narrow: true },
          cell: info => relationValueFor(info.row.original, relation.id) ?? '-',
        })
      ),

      column.display({
        id: 'menu',
        header: '',
        meta: { narrow: true, center: true },
        cell: info => <IssueRowMenu issue={info.row.original} />,
      }),
    ],
    [
      admin,
      column,
      highSeverity,
      lowSeverity,
      mediumSeverity,
      referenceHeader,
      relationColumns,
      translations.actualCost,
      translations.archived,
      translations.caseHandler,
      translations.category,
      translations.closed,
      translations.created,
      translations.date,
      translations.dateClosed,
      translations.dateEnd,
      translations.employee,
      translations.estimatedCost,
      translations.freightCarrier,
      translations.location,
      translations.open,
      translations.process,
      translations.responsible,
      translations.riskAspect,
      translations.severity,
      translations.started,
      translations.status,
      translations.subcategory,
      translations.tags,
      translations.title,
    ]
  )

  const hiddenColumns = useMemo(() => {
    const hidden: Record<string, boolean> = {}
    for (const name of Object.values(IssueTableColumn))
      hidden[name] = !isVisible(name)

    return hidden
  }, [isVisible])

  const sorting = useMemo<SortingState>(() => {
    const desc = filter.orderBy.startsWith('-')
    const orderField = desc ? filter.orderBy.slice(1) : filter.orderBy

    const sorted = Object.entries(ORDER_FIELDS).find(
      ([, field]) => field === orderField
    )
    if (!sorted) return []

    return [{ id: sorted[0], desc }]
  }, [filter.orderBy])

  const handleSortingChange = useCallback(
    (next: SortingState) => {
      const [sort] = next
      if (!sort) {
        onSort(DEFAULT_ORDER_BY)
        return
      }

      const orderField = ORDER_FIELDS[sort.id as keyof typeof ORDER_FIELDS]
      if (!orderField) return

      onSort(sort.desc ? (`-${orderField}` as IssueOrderFields) : orderField)
    },
    [onSort]
  )

  const filterActive =
    debouncedQuery !== '' ||
    debouncedArchived ||
    debouncedStatuses.length > 0 ||
    debouncedExcludeCategories.length !== 0
  const noDataText = filterActive
    ? translations.noResults
    : translations.noIssues

  useEffect(() => {
    if (!onFilterUpdate) return

    const amount = data?.allIssues?.total
    onFilterUpdate({ count: amount ?? undefined })
  }, [data?.allIssues?.total, onFilterUpdate])

  if (isMobile)
    return (
      <IssuesCards
        issues={issues}
        queryLoading={queryLoading}
        onLoadMore={handleScrollBottom}
      />
    )

  return (
    <DataTable
      columns={columns}
      data={issues}
      hiddenColumns={hiddenColumns}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      manualSorting
      loading={queryLoading ? 10 : false}
      noDataText={noDataText}
      rowClassName={issue => cn(issue.closed && 'bg-secondary/5')}
      inCard
    />
  )
}
